import {
  type ConnectionStatus,
  type HealthCheckResponse,
  APP_VERSION,
  HEALTH_CHECK_RETRY_COUNT,
  HEALTH_CHECK_RETRY_DELAY_MS,
} from '@idle-depths/shared';
import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';

/**
 * Application-level state store.
 *
 * Manages:
 * - Backend connection status
 * - Global loading state
 * - Application errors
 * - Health check responses
 */
export class AppStore {
  /** Current connection status to the backend */
  connectionStatus: ConnectionStatus = 'connecting';

  /** Whether the app is in a global loading state */
  isLoading = true;

  /** Global error message, if any */
  errorMessage: string | null = null;

  /** Last successful health check response */
  lastHealthCheck: HealthCheckResponse | null = null;

  /** Number of failed connection attempts */
  connectionAttempts = 0;

  constructor(_rootStore: RootStore) {
    makeAutoObservable(this);
  }

  /**
   * Whether the backend is currently connected.
   */
  get isConnected(): boolean {
    return this.connectionStatus === 'connected';
  }

  /**
   * Whether we're currently trying to connect.
   */
  get isConnecting(): boolean {
    return this.connectionStatus === 'connecting';
  }

  /**
   * Current application version.
   */
  get version(): string {
    return APP_VERSION;
  }

  /**
   * Set the connection status.
   */
  setConnectionStatus(status: ConnectionStatus): void {
    this.connectionStatus = status;
  }

  /**
   * Set the loading state.
   */
  setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  /**
   * Set an error message.
   */
  setError(message: string | null): void {
    this.errorMessage = message;
    if (message) {
      this.connectionStatus = 'error';
    }
  }

  /**
   * Clear the current error.
   */
  clearError(): void {
    this.errorMessage = null;
  }

  /**
   * Update state from a successful health check.
   */
  setHealthCheckResult(result: HealthCheckResponse): void {
    this.lastHealthCheck = result;
    this.connectionStatus = result.status === 'healthy' ? 'connected' : 'disconnected';
    this.connectionAttempts = 0;
    this.errorMessage = null;
  }

  /**
   * Handle a failed health check attempt.
   */
  handleConnectionFailure(error: Error): void {
    this.connectionAttempts += 1;

    if (this.connectionAttempts >= HEALTH_CHECK_RETRY_COUNT) {
      this.connectionStatus = 'error';
      this.errorMessage = `Failed to connect to server: ${error.message}`;
    }
  }

  /**
   * Check if we should retry connection.
   */
  get shouldRetryConnection(): boolean {
    return (
      this.connectionAttempts < HEALTH_CHECK_RETRY_COUNT &&
      (this.connectionStatus === 'connecting' || this.connectionStatus === 'disconnected')
    );
  }

  /**
   * Get the delay before next retry.
   */
  get retryDelay(): number {
    return HEALTH_CHECK_RETRY_DELAY_MS * Math.pow(2, this.connectionAttempts);
  }

  /**
   * Reset the store to initial state.
   */
  reset(): void {
    runInAction(() => {
      this.connectionStatus = 'connecting';
      this.isLoading = true;
      this.errorMessage = null;
      this.lastHealthCheck = null;
      this.connectionAttempts = 0;
    });
  }
}
